import { useEffect } from "react";
import { useNavigate } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ROUTES } from "@/app/routes";
import { useGetApiAgents } from "@/lib/api/generated/agents/agents";
import { usePostApiMcpToolsetsInstall } from "@/lib/api/generated/mcp-toolsets/mcp-toolsets";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";
import { toastMutationError } from "@/lib/utils/formatting";

const createMcpToolsetSchema = z
  .object({
    key: z
      .string()
      .min(1, "Key is required")
      .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Use lowercase words separated by hyphens."),
    displayName: z.string().min(1, "Display name is required"),
    description: z.string().optional(),
    serverUrl: z.string().url("Server URL must be a valid URL"),
    authMode: z.enum(["resource_token", "bearer", "none"]),
    serviceToken: z.string().optional(),
    agentId: z.string().min(1, "Agent is required"),
  })
  .superRefine((value, context) => {
    if (value.authMode === "bearer" && !value.serviceToken?.trim()) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["serviceToken"],
        message: "Credential is required.",
      });
    }
  });

type CreateMcpToolsetForm = z.infer<typeof createMcpToolsetSchema>;

export function CreateMcpToolsetPage() {
  const { tenantId } = useTenantContext();
  const navigate = useNavigate();
  const { data: agents = [], isLoading: agentsLoading } = useGetApiAgents({
    query: { enabled: Boolean(tenantId) },
  });
  const mutation = usePostApiMcpToolsetsInstall();

  const {
    register,
    handleSubmit,
    watch,
    getValues,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CreateMcpToolsetForm>({
    resolver: zodResolver(createMcpToolsetSchema),
    defaultValues: {
      key: "",
      displayName: "",
      description: "",
      serverUrl: "",
      authMode: "resource_token",
      serviceToken: "",
      agentId: "",
    },
  });

  const authMode = watch("authMode");

  useEffect(() => {
    const firstAgentId = agents[0]?.agentId;
    if (!getValues("agentId") && firstAgentId) {
      setValue("agentId", firstAgentId);
    }
  }, [agents, getValues, setValue]);

  const onSubmit = async (data: CreateMcpToolsetForm) => {
    try {
      const response = await mutation.mutateAsync({
        data: {
          key: data.key,
          displayName: data.displayName,
          description: data.description?.trim() || null,
          serverUrl: data.serverUrl,
          transport: "streamable-http",
          authMode: data.authMode,
          serviceToken: data.authMode === "bearer" ? data.serviceToken?.trim() : null,
          agentId: data.agentId,
        },
      });
      toast.success(`MCP server registered with ${response.tools.length} tools`);
      navigate(ROUTES.tenantConnectorInstalls(tenantId));
    } catch (error) {
      toast.error(toastMutationError(error, "Failed to register MCP server"));
    }
  };

  return (
    <div className="max-w-xl mx-auto">
      <h1 className="text-lg font-semibold mb-6" style={{ color: "var(--text-strong)" }}>
        Register MCP Server
      </h1>
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <FormField label="Key" error={errors.key?.message}>
              <input {...register("key")} className="form-input font-mono" placeholder="b12crm" />
            </FormField>

            <FormField label="Display name" error={errors.displayName?.message}>
              <input {...register("displayName")} className="form-input" placeholder="B12 CRM" />
            </FormField>

            <FormField label="Description" error={errors.description?.message}>
              <textarea {...register("description")} rows={3} className="form-input" />
            </FormField>

            <FormField label="Server URL" error={errors.serverUrl?.message}>
              <input
                {...register("serverUrl")}
                className="form-input font-mono"
                placeholder="https://example.com/mcp"
              />
            </FormField>

            <FormField label="Agent" error={errors.agentId?.message}>
              <select {...register("agentId")} className="form-input" disabled={agentsLoading}>
                <option value="">Select agent</option>
                {agents.map((agent) => (
                  <option key={agent.agentId} value={agent.agentId}>
                    {agent.name ?? agent.agentId}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="Auth mode" error={errors.authMode?.message}>
              <select {...register("authMode")} className="form-input">
                <option value="resource_token">Agentiq resource token</option>
                <option value="bearer">Bearer token</option>
                <option value="none">No authentication</option>
              </select>
            </FormField>

            {authMode === "bearer" ? (
              <FormField label="Bearer token" error={errors.serviceToken?.message}>
                <input
                  {...register("serviceToken")}
                  className="form-input font-mono"
                  type="password"
                  autoComplete="off"
                />
              </FormField>
            ) : null}

            <div className="flex items-center gap-3 pt-2">
              <Button type="submit" disabled={isSubmitting || agents.length === 0}>
                {isSubmitting ? "Registering..." : "Register MCP Server"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate(ROUTES.tenantConnectorInstalls(tenantId))}
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function FormField({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>
        {label}
      </label>
      {children}
      {error ? (
        <span className="text-xs" style={{ color: "var(--danger)" }}>
          {error}
        </span>
      ) : null}
    </div>
  );
}
